<template>
  <el-tooltip
    v-if="isOverflow"
    placement="bottom"
    popper-class="text-base"
    :popper-style="{ width: width + 'px', maxHeight: '500px', overflow: 'auto' }"
  >
    <span
      ref="textRef"
      :class="[textClass, lineClampCssClass]"
      :style="lineClampStyle"
    >
      {{ text }}
    </span>
    <template #content>
      <div class="text-base">
        {{ text }}
      </div>
    </template>
  </el-tooltip>
  <span
    v-else
    ref="textRef"
    :class="[textClass, lineClampCssClass]"
    :style="lineClampStyle"
  >
    {{ text }}
  </span>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch, nextTick, computed, useAttrs } from 'vue'

defineOptions({ inheritAttrs: false })

type Props = {
  text: string
  /** 超出几行后省略，1 为单行省略 */
  lineClamp?: number
  width?: number
}

const props = withDefaults(defineProps<Props>(), {
  text: '',
  lineClamp: 1,
  width: 210,
})

const attrs = useAttrs()

const textRef = ref<HTMLElement | null>(null)
const isOverflow = ref(false)
let resizeObserver: ResizeObserver | null = null

const textClass = computed(() => {
  const base = 'inline-block w-full overflow-hidden text-ellipsis'
  const lineClampPart = props.lineClamp <= 1
    ? 'whitespace-nowrap'
    : 'break-words'
  const custom = (typeof attrs.class === 'string' ? attrs.class : '').trim()
  return [base, lineClampPart, custom].filter(Boolean).join(' ')
})

const lineClampCssClass = computed(() => {
  if (props.lineClamp <= 1) return ''
  return 'ellipsis-line-clamp'
})

const lineClampStyle = computed(() => {
  if (props.lineClamp <= 1) return undefined
  return {
    WebkitLineClamp: props.lineClamp,
    lineClamp: props.lineClamp,
  } as const
})

const checkOverflow = async () => {
  await nextTick()
  if (!textRef.value) return
  if (props.lineClamp <= 1) {
    isOverflow.value = textRef.value.scrollWidth > textRef.value.clientWidth
  } else {
    isOverflow.value = textRef.value.scrollHeight > textRef.value.clientHeight
  }
}

onMounted(() => {
  checkOverflow()

  if (textRef.value && typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(() => {
      checkOverflow()
    })
    resizeObserver.observe(textRef.value)
  }
})

onUnmounted(() => {
  if (resizeObserver) {
    resizeObserver.disconnect()
    resizeObserver = null
  }
})

watch([() => props.text, () => props.lineClamp], () => {
  checkOverflow()
})
</script>

<style scoped>
.ellipsis-line-clamp {
  display: -webkit-box;
  -webkit-box-orient: vertical;
}
</style>

